import DemoCaption from "@/components/preview/demo-caption";
import { Frame } from "@/components/ui/frame";
import { cn } from "@/lib/utils";

type HeightValue = number | string;

export type DemoHeight = HeightValue | { base?: HeightValue; sm?: HeightValue };

type DemoAlign = "center" | "stretch";

const alignClasses: Record<DemoAlign, string> = {
  center: "items-center justify-center",
  stretch: "",
};

interface DemoWrapperProps {
  captionHref?: string;
  captionText?: string;
  children: React.ReactNode;
  className?: string;
  height?: DemoHeight;
  margin?: string;
  ref?: React.Ref<HTMLDivElement>;
}

interface NestedDemoProps extends DemoWrapperProps {
  align?: DemoAlign;
  innerClassName?: string;
}

export const HEIGHT_CLASSES = "h-(--dw-h-base) sm:h-(--dw-h-sm)";

function toPx(value: HeightValue | undefined) {
  return typeof value === "number" ? `${value}px` : value;
}

export function heightStyle(height: DemoHeight | undefined) {
  if (height === undefined) {
    return;
  }
  const { base, sm } = typeof height === "object" ? height : { base: height };
  return {
    "--dw-h-base": toPx(base) ?? "auto",
    "--dw-h-sm": toPx(sm ?? base) ?? "auto",
  } as React.CSSProperties;
}

function DemoRoot({
  captionHref,
  captionText,
  children,
  margin = "my-8",
}: Pick<
  DemoWrapperProps,
  "captionHref" | "captionText" | "children" | "margin"
>) {
  return (
    <div className={cn(margin, "w-full")}>
      {children}
      {captionText && (
        <DemoCaption href={captionHref}>{captionText}</DemoCaption>
      )}
    </div>
  );
}

function surfaceClassName(
  height: DemoHeight | undefined,
  className: string | undefined
) {
  return cn("preview-card", height !== undefined && HEIGHT_CLASSES, className);
}

export default function DemoWrapper({
  captionHref,
  captionText,
  children,
  className,
  height,
  margin,
  ref,
}: DemoWrapperProps) {
  return (
    <DemoRoot
      captionHref={captionHref}
      captionText={captionText}
      margin={margin}
    >
      <div
        className={surfaceClassName(height, className)}
        ref={ref}
        style={heightStyle(height)}
      >
        {children}
      </div>
    </DemoRoot>
  );
}

export function NestedDemo({
  align = "stretch",
  captionHref,
  captionText,
  children,
  className,
  height,
  innerClassName,
  margin,
  ref,
}: NestedDemoProps) {
  return (
    <DemoRoot
      captionHref={captionHref}
      captionText={captionText}
      margin={margin}
    >
      <Frame
        className={surfaceClassName(height, className)}
        radius="demo"
        ref={ref}
        style={heightStyle(height)}
      >
        <div
          className={cn(
            "flex h-full w-full overflow-hidden rounded-lg bg-preview-bg shadow-custom",
            alignClasses[align],
            innerClassName
          )}
        >
          {children}
        </div>
      </Frame>
    </DemoRoot>
  );
}
